// @flow
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
	DataGridPremium,
	gridFilterModelSelector,
	gridSortModelSelector, gridVisibleColumnDefinitionsSelector,
	useGridApiRef
} from "@mui/x-data-grid-premium";
import axios from "axios";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import PageContentWrapper from "../../../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../../../components/mui/app/pageTitle/pageTitle";
import QuickView from "../../../../components/mui/general/quickView/quickView";
import SkeletonLoaderWrapper from "../../../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { CustomPagination } from "../../../../components/mui/grids/dataGridCustom/customPagination";
import CustomToolbar from "../../../../components/mui/grids/dataGridCustom/customToolbar/customToolbar";
import ToolbarWithState from "../../../../components/mui/grids/dataGridCustom/toolbarWithState/toolbarWithState";
import DataGridWrapper from "../../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../../helpers/errorHandling";
import { downloadFile } from "../../../../helpers/fileHandle";
import { mapDateRange } from "../../../../helpers/helpers";
import { muiGridLocales } from "../../../../helpers/mui/muiLocales";
import {
	access_history,
	history_column_assignee,
	history_column_actual_charge,
	history_column_charge,
	history_column_customer,
	history_column_erp_code,
	history_column_estimated_end,
	history_column_project,
	edit_issue_add_items,
	issue_add_items,
	view_po
} from "../../../../helpers/rights/rights";
import { numericFormatDigitDefaults } from "../../../../helpers/theme/defaultProps";
import useFiltersData from "../../../../hooks/dashboard/useFiltersData";
import { useMuiGridServerSide } from "../../../../hooks/muiGrid/useMuiGridServerSide";
import useUserAction from "../../../../hooks/user/useUserAction";
import useUserData from "../../../../hooks/user/useUserData";
import CardChip from "../../../../pages/dashboard/partials/cardChip";
import TruncateDescription from "../../../../pages/dashboard/partials/truncateDescription";
import PermissionHOC, { withPermissionHOC } from "../../../permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../../permission/hooks/useHasPermission";
import IssueFilters from "../../components/issueFilters/issueFilters";
import IssueQuickView from "../../components/quickDetails/issueQuickView";
import { ISSUES_GLOBAL } from "../../locales/namespaces";
import PO from "../../components/PO/PO";
import PoQuickView from "../../components/PO/poQuickView";
import SOCust from "../../components/SOCust/SOCust";
import SoCustQuickView from "../../components/SOCust/soCustQuickView";
import SelectedPODialog from "../../components/PO/selectedPODialog";
import SelectedSODialog from "../../components/SOCust/selectedSODialog";
import PoCustQuickView from "../../components/POcust/poCustQuickView";

type Props = {};

export const GridToolbar = ({ exportData, t, loading }) => {
	return (
		<ToolbarWithState
			code={'portal_issues_index'}
			showDensity={false}
			showExport={false}
		>
			<Button
				onClick={exportData}
				loading={loading}
			>
				{t('Export')}
			</Button>
		</ToolbarWithState>
	)
}

export const initialFilters = {
	customerFilter: null,
	siteFilter: [],
	targetedStartDt: [],
	targetedEndDt: [],
	actualEndDt: [],
	submitDt: [],
	statusFilter: [],
	typeFilter: [],
	codeFilter: '',
	categoriesFilter: [],
	assigneesFilter: [],
	internalAssignmentsFilter: [],
	kindFilter: [],
	technicianFilter:[]
}

const mapSortModel = model => {
	return model.map(f => {
		if (f.name === 'Priority') return { name: 'PriorityOrder', direction: f.direction }
		return f
	})
}

export const mapFilters = (filters) => {
	const {
		customerFilter,
		siteFilter,
		typeFilter,
		codeFilter,
		categoriesFilter,
		assigneesFilter,
		internalAssignmentsFilter,
		kindFilter,
		technicianFilter,
		targetedStartDt,
		targetedEndDt,
		actualEndDt,
		submitDt,
		statusFilter
	} = filters
	const [targetedStartDtFrom, targetedStartDtTo] = mapDateRange(targetedStartDt)
	const [targetedEndDtFrom, targetedEndDtTo] = mapDateRange(targetedEndDt)
	const [actualEndFrom, actualEndTo] = mapDateRange(actualEndDt)
	const [submitDtFrom, submitDtTo] = mapDateRange(submitDt)
	return {
		...(customerFilter && customerFilter?.id && { customerFilter: [{ id: customerFilter.id }] }),
		...(statusFilter.length > 0 && { statusFilter: statusFilter }),
		...(typeFilter.length > 0 && { typeFilter: typeFilter }),
		...(siteFilter.length > 0 && { siteFilter: siteFilter }),
		...(codeFilter && { codeFilter: codeFilter }),
		...(categoriesFilter.length > 0 && { categoriesFilter: categoriesFilter }),
		...(assigneesFilter.length > 0 && { assigneesFilter: assigneesFilter }),
		...(internalAssignmentsFilter?.length > 0 && { internalAssignmentsFilter: internalAssignmentsFilter }),
		...(kindFilter?.length > 0 && { kindFilter: kindFilter }),
		...(technicianFilter?.length > 0 && { technicianFilter: technicianFilter }),
		...(targetedStartDtFrom && { TargetedStartDtFrom: targetedStartDtFrom }),
		...(targetedStartDtTo && { TargetedStartDtTo: targetedStartDtTo }),
		...(targetedEndDtFrom && { TargetedEndDtFrom: targetedEndDtFrom }),
		...(targetedEndDtTo && { TargetedEndDtTo: targetedEndDtTo }),
		...(actualEndFrom && { ActualEndDtFrom: actualEndFrom }),
		...(actualEndTo && { ActualEndDtTo: actualEndTo }),
		...(submitDtFrom && { SubmitDtFrom: submitDtFrom }),
		...(submitDtTo && { SubmitDtTo: submitDtTo })
	}
}

const Index = (props: Props) => {
	const apiRef = useGridApiRef();
	const { token, isLoggedIn, language, dateFormat, timeFormat, decimalSeparator, thousandSeparator } = useUserData();
	const { userLogout } = useUserAction();
	const { t } = useTranslation(ISSUES_GLOBAL)
	const [filters, setFilters] = useState(initialFilters)
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [openPO,setOpenPO] = useState(false)
	const [dataPO,setDataPO] = useState([])
	const [openSO,setOpenSO] = useState(false)
	const [dataSO,setDataSO] = useState([])
	const [loadingExport, setLoadingExport] = useState(false)
	const [openSelectedPO,setOpenSelectedPO] = useState(false)
	const [openSelectedSO,setOpenSelectedSO] = useState(false)
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		onGroupModelChange,
		dataManager,
		convertSortModel,
		convertFilterModel
	} = useMuiGridServerSide({
		url: 'issues/index',
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }],
		otherDataParams: {
			...mapFilters(filters)
		}
	})
	
	const { loading: loadingFilterData, sites, customers, statuses, types, code, categories, assignees, kinds,technicians, internalAssignments, setFiltersData } = useFiltersData()
	const viewAssigneeColumn = useHasPermission([history_column_assignee], { shouldView: true })
	const viewCustomerColumn = useHasPermission([history_column_customer], { shouldView: true })
	const viewErpCodeColumn = useHasPermission([history_column_erp_code], { shouldView: true })
	const viewProjectColumn = useHasPermission([history_column_project], { shouldView: true })
	const viewChargeColumn = useHasPermission([history_column_charge], { shouldView: true })
	const viewActualChargeColumn = useHasPermission([history_column_actual_charge], { shouldView: true })
	const viewEstimatedEndColumn = useHasPermission([history_column_estimated_end], { shouldView: true })
	const viewPO = useHasPermission([view_po], { shouldView: true })

	const selectionProps = viewPO
		? {
			checkboxSelection: true,
			disableRowSelectionOnClick: true,
			rowSelectionModel,
			onRowSelectionModelChange: (newModel) => setRowSelectionModel(newModel),
		}
		: {

			disableRowSelectionOnClick: true,
		};

	useEffect(() => {
		if (!viewPO) setRowSelectionModel([]);
	}, [viewPO]);


	const [lastSearchFilters, setLastSearchFilters] = useState(null);

	useEffect(() => {
		setLastSearchFilters(null);
		setRowSelectionModel([]);
	}, [
		filters.customerFilter,
		filters.siteFilter,
		filters.targetedStartDt,
		filters.targetedEndDt,
		filters.actualEndDt,
		filters.submitDt,
		filters.statusFilter,
		filters.typeFilter,
		filters.codeFilter,
		filters.categoriesFilter,
		filters.assigneesFilter,
		filters.internalAssignmentsFilter
	]);



	useEffect(() => {
		document.title = t('Issues')
	}, [t])
	
	const [issueQuickView, setIssueQuickView] = useState({
		open: false,
		issueId: null
	})
	const [poQuickView, setPoQuickView] = useState({
		open: false,
		Code: null
	})
	const [soCustQuickView, setSoCustQuickView] = useState({
		open: false,
		Code: null
	})
	const [poCustQuickView, setPoCustQuickView] = useState({
		open: false,
		Code: null
	})
	const closeQuickViewIssue = () => {
		setIssueQuickView({
			open: false,
			issueId: null
		})
	}
	const closeQuickViewPo = () => {
		setPoQuickView({
			open: false,
			Code: null
		})
	}

	const closeQuickViewSoCust = () => {
		setSoCustQuickView({
			open: false,
			Code: null
		})
	}
	const closeQuickViewPoCust = () => {
		setPoCustQuickView({
			open: false,
			Code: null
		})
	}
	
	const exportData = useCallback(() => {
		const sortModel = gridSortModelSelector(apiRef)
		const where = gridFilterModelSelector(apiRef)
		const columns = gridVisibleColumnDefinitionsSelector(apiRef)
		const data = {
			sorted: convertSortModel(sortModel, mapSortModel),
			where: convertFilterModel(where),
			columns: columns.filter(f => f.type !== 'custom' && f.type !== 'actions').map(i => ({
				id: i.field,
				title: i.headerName
			})),
			...mapFilters(filters)
		}
		setLoadingExport(true)
		axios({
			url: `${API}/en/issue/table/export`,
			method: 'post',
			responseType: "blob",
			data: data,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		}).then((response) => {
			const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const href = window.URL.createObjectURL(blob)
			downloadFile(href, 'export-visitors')
			setLoadingExport(false)
		}).catch((error) => {
			setLoadingExport(false)
			const errorMessage = error?.status === 404 ? 'No data found to export!' : 'Export failed'
			enqueueSnackbar(handleAxiosErrors(error, t(errorMessage)), {
				variant: 'error',
				autoHideDuration: null,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [apiRef, convertSortModel, convertFilterModel, t, isLoggedIn, userLogout, filters])
	
	const columns = useMemo(() => [
		{
			field: 'Code',
			headerName: t('Code'),
			description: t('Code'),
			minWidth: 140,
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Button
						variant={'link'}
						onClick={() => {
							setIssueQuickView({
								open: true,
								issueId: row.id
							})
						}}
					>
						{row.Code}
					</Button>
				)
			}
		},
		...(viewErpCodeColumn ? [{
			field: 'ErpCode',
			headerName: t('Erp Code'),
			description: t('Erp Code'),
			minWidth: 100,
			flex: 1,
		}] : []),
		...(viewPO ? [{
			field: 'POCode',
			headerName: t('PO Code'),
			description: t('PO Code'),
			minWidth: 100,
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Button
						variant={'link'}
						onClick={() => {
							setPoQuickView({
								open: row.POCode != null,
								Code: row.POCode
							})
						}}
					>
						{row.POCode}
					</Button>
				)
			}
		}] : []),
		...(viewPO ? [{
			field: 'SOCode',
			headerName: t('SO Code'),
			description: t('SO Code'),
			minWidth: 100,
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Button
						variant={'link'}
						onClick={() => {
							setSoCustQuickView({
								open: row.SOCode != null,
								Code: row.SOCode
							})
						}}
					>
						{row.SOCode}
					</Button>
				)
			}
		}] : []),
		...(viewPO ? [{
			field: 'CPOCode',
			headerName: t('PO Cust Code'),
			description: t('PO Cust Code'),
			minWidth: 100,
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Button
						variant={'link'}
						onClick={() => {
							setPoCustQuickView({
								open: row.CPOCode != null,
								Code: row.CPOCode
							})
						}}
					>
						{row.CPOCode}
					</Button>
				)
			}
		}] : []),
		{
			field: 'ErpCode2',
			headerName: t('Reference Code'),
			description: t('Reference Code'),
			minWidth: 110,
			flex: 1
		},
		{
			field: 'Description',
			headerName: t('Description'),
			description: t('Description'),
			minWidth: 300,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'Summary',
			headerName: t('Summary'),
			description: t('Summary'),
			minWidth: 300,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'AddressErpCode',
			headerName: t('Site Code'),
			description: t('Site Code'),
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'Site',
			headerName: t('Site'),
			description: t('Site'),
			minWidth: 250,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'Address',
			headerName: t('Address'),
			description: t('Address'),
			minWidth: 250,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		...(viewCustomerColumn ? [{
			field: 'CustName',
			headerName: t('Customer'),
			description: t('Customer'),
			minWidth: 250,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		}] : []),
		{
			field: 'ContactName',
			headerName: t('Contact'),
			description: t('Contact'),
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'Status',
			headerName: t('Status'),
			description: t('Status'),
			flex: 1,
			minWidth: 200,
			renderCell: ({ row }) => {
				return row.Status && (
					<Box sx={{
						display: 'flex', justifyContent: 'center', alignItems: 'center', width:
							'100%'
					}}>
						<CardChip
							label={row.Status}
							color={row?.StatusColor}
							borderColor={row?.StatusColor}
						/>
					</Box>
				)
			}
		},
		{
			field: 'Priority',
			headerName: t('Priority'),
			description: t('Priority'),
			flex: 1,
			minWidth: 120,
			renderCell: ({ row }) => {
				return row.Priority && (
					<Box sx={{
						display: 'flex', justifyContent: 'center', alignItems: 'center', width:
							'100%'
					}}>
						<CardChip
							label={row.Priority}
							color={row?.PriotiryColor}
							borderColor={row?.PriotiryColor}
						/>
					</Box>
				)
			}
		},
		{
			field: 'Type',
			headerName: t('Dimension'),
			description: t('Dimension'),
			flex: 1,
			minWidth: 100,
			renderCell: ({ row }) => {
				return row.Type && (
					<Box sx={{
						display: 'flex', justifyContent: 'center', alignItems: 'center', width:
							'100%'
					}}>
						<CardChip
							label={row.Type}
							color={row?.TypeColor}
							borderColor={row?.TypeColor}
						/>
					</Box>
				)
			}
		},
		{
			field: 'Kind',
			headerName: t('Kind'),
			description: t('Kind'),
			flex: 1,
			minWidth: 120,
		},
		{
			field: 'ItemCategDescription',
			headerName: t('Service Category'),
			description: t('Service Category'),
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'ItemDescription',
			headerName: t('Service'),
			description: t('Service'),
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		...(viewAssigneeColumn ? [{
			field: 'Assignee',
			headerName: t('Subcontractor'),
			description: t('Subcontractor'),
			flex: 1,
			minWidth: 150,
		}] : []),
		...(viewProjectColumn ? [{
			field: 'PrjDescription',
			headerName: t('Project'),
			minWidth: 180,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		}] : []),
		{
			field: 'InternalAssignment',
			headerName: t('Internal Assignment'),
			description: t('Internal Assignment'),
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},{
			field: 'TechnicianName',
			headerName: t('Technician'),
			description: t('Technician'),
			flex: 1,
			minWidth: 200,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		...(viewActualChargeColumn ? [{
			field: 'ActualCost',
			headerName: t('Customer Cost'),
			description: t('Customer Cost'),
			type: 'number',
			minWidth: 100,
			flex: 1,
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		}] : []),
		...(viewChargeColumn ? [{
			field: 'ChargeCost',
			headerName: t('Subcontractor Charge'),
			description: t('Subcontractor Charge'),
			minWidth: 100,
			type: 'number',
			flex: 1,
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		}] : []),
		{
			field: 'SubmitDt',
			headerName: t('Submit Dt'),
			description: t('Submit Dt'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'TargetedStartDt',
			headerName: t('Targeted Start'),
			description: t('Targeted Start'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		},
		{
			field: 'TargetedStartTm',
			headerName: t('Targeted Start Time'),
			description: t('Targeted Start Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualVisitDt',
			headerName: t('Actual Visit Date'),
			description: t('Actual Visit Date'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		},
		{
			field: 'ActualVisitTm',
			headerName: t('Actual Visit Time'),
			description: t('Actual Visit Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'TargetedEndDt',
			headerName: t('Targeted Expiry'),
			description: t('Targeted Expiry'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		},
		{
			field: 'TargetedEndTm',
			headerName: t('Targeted Expiry Time'),
			description: t('Targeted Expiry Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualStartDt',
			headerName: t('Actual Start'),
			description: t('Actual Start'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualEndDt',
			headerName: t('Actual End'),
			description: t('Actual End'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'EstimStartDt',
			headerName: t('Est. Start'),
			description: t('Est. Start'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		},
		{
			field: 'EstimStartTm',
			headerName: t('Est. Start Time'),
			description: t('Est. Start Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		...(viewProjectColumn ? [{
			field: 'EstimDueDt',
			headerName: t('Est. Expiry'),
			description: t('Est. Expiry'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		}] : []),
		...(viewProjectColumn ? [{
			field: 'EstimDueTm',
			headerName: t('Est. Expiry Time'),
			description: t('Est. Expiry Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		}] : []),
		{
			field: 'EstimDuration',
			type: 'number',
			headerName: t('Duration (Days)'),
			description: t('Duration (Days)'),
			flex: 1,
			minWidth: 120,
			valueGetter: (value, row, column, apiRef) => value ? Number(value) / 60 : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		},
		{
			field: 'EstimWork',
			type: 'number',
			headerName: t('Est. Work'),
			description: t('Est. Work'),
			flex: 1,
			minWidth: 120,
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		},
		{
			field: 'PCTComplete',
			type: 'number',
			headerName: t('Completed (%)'),
			description: t('Completed (%)'),
			flex: 1,
			minWidth: 120,
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		},
		{
			field: 'isBillable',
			headerName: t('Billable'),
			description: t('Billable'),
			flex: 1,
			minWidth: 90,
			type: 'boolean',
			valueGetter: (value, row, column, apiRef) => value === '1'
		},
	], [t, dateFormat, viewErpCodeColumn, viewCustomerColumn, viewAssigneeColumn, viewProjectColumn, viewActualChargeColumn, decimalSeparator, viewChargeColumn])

	const REQUIRED_STATUS_IDS = ["7", "3", "5"];
	const REQUIRED_STATUS_IDS_PO = ["7"];

	const getSingleId = (v) => {
		if (v == null) return null;

		if (Array.isArray(v)) {
			if (v.length !== 1) return null;
			const item = v[0];
			if (item == null) return null;
			if (typeof item === "object") return item.id ?? null;
			return item;
		}

		if (typeof v === "object") return v.id ?? null;

		return v;
	};
	const getIds = (v) => {
		if (v == null) return [];

		if (Array.isArray(v)) {
			return v
				.map(item => {
					if (item == null) return null;
					if (typeof item === "object") return item.id ?? null;
					return item;
				})
				.filter(id => id != null)
				.map(String);
		}

		if (typeof v === "object") {
			return v.id != null ? [String(v.id)] : [];
		}

		return [String(v)];
	};

	const hasRunSearch = !!lastSearchFilters;
	const statusIds = getIds(lastSearchFilters?.statusFilter);
	const statusId = getSingleId(lastSearchFilters?.statusFilter);
	const assigneeId = getSingleId(lastSearchFilters?.assigneesFilter);
	const customerId = getSingleId(lastSearchFilters?.customerFilter);

	const statusOk =
		hasRunSearch &&
		statusIds.length > 0 &&
		statusIds.every(id => REQUIRED_STATUS_IDS.includes(id));

	const statusOkPO =
		hasRunSearch &&
		statusId != null &&
		REQUIRED_STATUS_IDS_PO.includes(String(statusId));

	const assigneeOk =
		hasRunSearch &&
		assigneeId != null;

	const custOk =
		hasRunSearch &&
		customerId != null;

	const hasSelectedRows = rowSelectionModel.length > 0;


	const handlePO = () => {

		if (!hasRunSearch) {
			enqueueSnackbar(t("Please run a search first!"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}

		if (!assigneeOk || !statusOkPO) {

			enqueueSnackbar(t("Select exactly one assignee and status"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}


		if (!hasSelectedRows) {
			enqueueSnackbar(t("Select at least one row from the results"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}

		const selectedRows = rowSelectionModel
			.map((id) => apiRef.current.getRow(id))
			.filter(Boolean);

		const rowsWithPOCode = selectedRows.filter((r) => {
			const po = r?.POCode;

			return po !== null && po !== undefined && String(po).trim() !== "";
		});

		if (rowsWithPOCode.length > 0) {
			enqueueSnackbar(
				t("Some selected rows already have a PO Code. Please deselect them to proceed."),
				{
					variant: "warning",
					autoHideDuration: 3000,
				}
			);
			return;
		}

		setDataPO(selectedRows);
		setOpenSelectedPO(true);
	};

	const handleSO = () => {

		if (!hasRunSearch) {
			enqueueSnackbar(t("Please run a search first!"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}

		if (!custOk || !statusOk) {

			enqueueSnackbar(t("Select exactly one customer and status"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}


		if (!hasSelectedRows) {
			enqueueSnackbar(t("Select at least one row from the results"), {
				variant: "warning",
				autoHideDuration: 3000,
			});
			return;
		}

		const selectedRows = rowSelectionModel
			.map((id) => apiRef.current.getRow(id))
			.filter(Boolean);

		const rowsWithPOCustCode = selectedRows.filter((r) => {
			const poCust = r?.CPOCode;

			return poCust !== null && poCust !== undefined && String(poCust).trim() !== "";
		});
		const rowsWithSOCode = selectedRows.filter((r) => {
			const so = r?.SOCode;

			return so !== null && so !== undefined && String(so).trim() !== "";
		});


		if (rowsWithSOCode.length > 0) {
			enqueueSnackbar(
				t("Some selected rows already have a SO Code. Please deselect them to proceed."),
				{
					variant: "warning",
					autoHideDuration: 3000,
				}
			);
			return;
		}

		setDataSO(selectedRows);
		setOpenSelectedSO(true);
	};

	return (
		<Box className={'issues_index'}>
			<PageContentWrapper>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<PageTitle title={t("Issues")} />

					<Box
						sx={{
							mt: 1,
							width: {
								xl: "20%",
								lg: "25%",
								md: "35%",
								sm: "50%",
								xs: "100%",
							},
							minWidth: {
								sm: 320,
							},
						}}
					>
						<PermissionHOC requiredPermissions={[view_po]}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-evenly",
									gap: 1,
									flexWrap: "wrap",
								}}
							>
								{/*<Button variant="outlined" onClick={handleSO}>*/}
								{/*	{t("S.O")}*/}
								{/*</Button>*/}

								<Button variant="outlined" onClick={handleSO}>
									{t("S.O")}
								</Button>

								<Button variant="outlined" onClick={handlePO}>
									{t("P.O")}
								</Button>
							</Box>
						</PermissionHOC>
					</Box>
				</Box>

				<SkeletonLoaderWrapper loading={loadingFilterData}>
					<IssueFilters
						initialFilters={initialFilters}
						filters={filters}
						setFilters={setFilters}
						dataManager={dataManager}
						loading={isLoading}
						sites={sites}
						customers={customers}
						statuses={statuses}
						types={types}
						categories={categories}
						assignees={assignees}
						internalAssignments={internalAssignments}
						kinds={kinds}
						technicians={technicians}
						setFiltersData={setFiltersData}
						onSearchCompleted={() => setLastSearchFilters(JSON.parse(JSON.stringify(filters)))}

					/>
				</SkeletonLoaderWrapper>
				<DataGridWrapper sx={{ height: 700 }}>
					<DataGridPremium
						{...selectionProps}
						apiRef={apiRef}
						columns={columns}
						rows={data}
						pagination
						paginationModel={{ page, pageSize }}
						paginationMode="server"
						onPaginationModelChange={onPaginationModelChange}
						rowCount={Number(total)}
						pageSizeOptions={[5, 10, 15, 20, 50, 100]}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel, mapSortModel)}
						filterMode="server"
						onRowGroupingModelChange={onGroupModelChange}
						onFilterModelChange={onFilterModelChange}
						loading={isLoading}
						localeText={muiGridLocales(language)}
						initialState={{
							density: 'compact',
							columns: {
								columnVisibilityModel: {
									isBillable: false,
									Contact: false,
									Kind: false,
									EstimDuration: false,
									EstimWork: false,
									PCTComplete: false,
									ContactName: false,
									EstimStartTm: false,
									EstimDueTm: false,
									PrjDescription: false
								},
							},
						}}
						slots={{
							toolbar: GridToolbar,
							pagination: CustomPagination,
						}}
						slotProps={{
							toolbar: {
								exportData: exportData,
								t: t,
								loading: loadingExport
							}
						}}
					/>
				</DataGridWrapper>
			</PageContentWrapper>
			<QuickView
				autoClose={true}
				show={issueQuickView.open}
				onBackgroundClick={closeQuickViewIssue}
				sx={{
					maxWidth: '1450px'
				}}
			>
				<IssueQuickView
					issueId={issueQuickView.issueId}
					onClose={closeQuickViewIssue}
					loadingIndex={isLoading}
					dataManager={dataManager}
					setRowSelectionModel={setRowSelectionModel}
					filters={filters}
				/>
			
			</QuickView>
			<QuickView
				autoClose={true}
				show={poQuickView.open}
				onBackgroundClick={closeQuickViewPo}
				sx={{
					maxWidth: {
						xs:'380px',
						sm:'600px',
						md:'800px',
						lg:'1450px'
					}
				}}
			>
				<PoQuickView
					Code={poQuickView.Code}
					onClose={closeQuickViewPo}
					loadingIndex={isLoading}
					dataManager={dataManager}
					setRowSelectionModel={setRowSelectionModel}
					filters={filters}
				/>

			</QuickView>
			<QuickView
				autoClose={true}
				show={soCustQuickView.open}
				onBackgroundClick={closeQuickViewSoCust}
				sx={{
					maxWidth: {
						xs:'380px',
						sm:'600px',
						md:'800px',
						lg:'1450px'
					}
				}}
			>
				<SoCustQuickView
					Code={soCustQuickView.Code}
					onClose={closeQuickViewSoCust}
					loadingIndex={isLoading}
					dataManager={dataManager}
					setRowSelectionModel={setRowSelectionModel}
					filters={filters}
				/>

			</QuickView>
			<QuickView
				autoClose={true}
				show={poCustQuickView.open}
				onBackgroundClick={closeQuickViewPoCust}
				sx={{
					maxWidth: {
						xs:'380px',
						sm:'600px',
						md:'800px',
						lg:'1450px'
					}
				}}
			>
				<PoCustQuickView
					Code={poCustQuickView.Code}
					onClose={closeQuickViewPoCust}
					loadingIndex={isLoading}
					dataManager={dataManager}
					setRowSelectionModel={setRowSelectionModel}
					filters={filters}
				/>

			</QuickView>
			<PO
			data={dataPO}
			rowSelectionModel={rowSelectionModel}
			open={openPO}
			onClose={()=>setOpenPO(false)}
			dataManager={dataManager}
			setLastSearchFilters={setLastSearchFilters}
			setRowSelectionModel={setRowSelectionModel}
			setFilters={setFilters}
			initialFilters={initialFilters}
			filters={filters}
			loadingIndex={isLoading}
			/>
			<SOCust
				data={dataSO}
				rowSelectionModel={rowSelectionModel}
				open={openSO}
				onClose={()=>setOpenSO(false)}
				dataManager={dataManager}
				setLastSearchFilters={setLastSearchFilters}
				setRowSelectionModel={setRowSelectionModel}
				setFilters={setFilters}
				initialFilters={initialFilters}
				filters={filters}
				loadingIndex={isLoading}
			/>
			<SelectedPODialog
				rowSelectionModel={rowSelectionModel}
			open={openSelectedPO}
			onClose={()=>setOpenSelectedPO(false)}
			setOpenPO={setOpenPO}
				setPoQuickView={setPoQuickView}
				dataManager={dataManager}
				setRowSelectionModel={setRowSelectionModel}
				filters={filters}
			/>
			<SelectedSODialog
				rowSelectionModel={rowSelectionModel}
				open={openSelectedSO}
				onClose={()=>setOpenSelectedSO(false)}
				setOpenSO={setOpenSO}
				setSoCustQuickView={setSoCustQuickView}
				dataManager={dataManager}
				setRowSelectionModel={setRowSelectionModel}
				filters={filters}
			/>
		</Box>
	);
};

export default withPermissionHOC(Index, [access_history])